/**
 * V5 报告 mock —— **严格匹配后端契约 wrapper 形态**（docs/archive/v5/M5-integration-contract.md
 * 「API Contract」+ AI-HANDOFF.md 2026-05-20 backend 条目）。
 *
 * 数据来源：
 *  - LLM 文案 8 字段：字面拷贝 backend/apps/reports/schemas/v5_report_example.json
 *  - scoring (overall/abilities/layers/traits) / meta / user：前端合成 L2 人设，
 *    保持 strength/improvement 排名一致（passes validator）。
 *
 * 顶层 HTTP 包装：{status, report_version, is_fallback, user_report_v5: {...}}
 * fallback 变体见底部 mockReportFallbackPayload —— 用于前端验证降级 banner。
 *
 * ⚠️ 不要在这里扩展或修改字段；要改字段先回后端 schema。
 */

// ============================================================================
// LLM 文案 8 字段（字面拷贝自 v5_report_example.json）
// ============================================================================
const llmNarrative = {
  headline_tag: '基础探索型｜愿意尝试 AI，但判断与核查能力仍需加强。',
  overall_summary:
    '你已能在日常工作中主动使用 AI 完成具体任务，整体处于基础应用水平。在指令表达和方法尝试上具备一定积累，但在判断"这件事该不该交给 AI"以及"AI 给出的结果是否经得起核查"这两件事上仍偏弱。下一步如果能补足边界认知 + 建立结果复核动作，整体水平会比较自然地从基础应用迈向协同优化。',
  strength_abilities: ['learning_adaptation', 'instruction_interaction', 'innovation_transformation'],
  improvement_abilities: ['cognitive_understanding', 'result_evaluation', 'safety_responsibility'],
  priority_actions: ['补足 AI 边界认知', '建立结果核查习惯', '练习任务拆解方法'],
  comprehensive_interpretation:
    '你目前处于 AI 应用的基础应用阶段：能够主动把 AI 接入日常工作，在指令表达和方法迁移上表现稳定，也愿意尝试新的用法。这是一个不错的起点。当前真正限制你继续提升的，是两件事：一是对 AI 能做什么、不能做什么的边界还不够清晰，容易在不合适的任务上过度依赖它；二是拿到 AI 的结果后核查不足，较少做事实核对和逻辑复查。建议的切入路径是先补边界认知，再把结果核查变成固定动作——用前先判断这个任务该不该交给 AI，用后留出一步人工复核。把这两点稳住，你的整体水平会较快地从基础应用迈向协同优化。',
  try_first_scenarios: [
    { name: '资料整理与信息梳理', icon_hint: 'folder', reason: '信息零散、规则明确，AI 擅长归类', usage: '让 AI 先归类汇总，你核对关键项' },
    { name: '会议纪要初步整理', icon_hint: 'users', reason: '内容繁杂，AI 能快速提炼', usage: '让 AI 出纪要初稿，你补结论与待办' },
    { name: '邮件与通知初稿撰写', icon_hint: 'mail', reason: '格式固定、重复度高', usage: '让 AI 起草，你定口径再发出' },
    { name: '文案润色与改写', icon_hint: 'edit', reason: '表达优化类，改错了影响小', usage: '让 AI 给多版改写，你挑定稿' },
    { name: '学习新知识时的概念讲解', icon_hint: 'book-open', reason: '通用知识，AI 讲解直观', usage: '让 AI 打比方讲清，你再查证关键点' },
    { name: '重复性表格的格式处理', icon_hint: 'table', reason: '机械重复，AI 高效准确', usage: '让 AI 批量套格式，你抽查结果' },
  ],
  be_cautious_scenarios: [
    { name: '对外发布的正式内容', icon_hint: 'megaphone', reason: '对外口径错了影响大', boundary: 'AI 可拟稿，定稿必须人工把关' },
    { name: '真实客户资料分析', icon_hint: 'user-check', reason: '涉及真实客户数据', boundary: '数据脱敏后再用 AI，结论人工复核' },
    { name: '涉及数据与事实的结论', icon_hint: 'bar-chart', reason: 'AI 可能编造数字', boundary: '数据与事实一律人工复核' },
    { name: '正式决策的依据判断', icon_hint: 'scale', reason: '责任在己，不由 AI 下判', boundary: 'AI 仅整理事实，结论由你负责' },
    { name: '专业领域的准确性核对', icon_hint: 'shield', reason: '强专业场景需专业裁量', boundary: 'AI 供参考，专业结论人工核实' },
    { name: '敏感信息与隐私处理', icon_hint: 'lock', reason: '涉数据安全与合规', boundary: '敏感信息不入 AI，必要时用私有环境' },
  ],
  priority_improvements: [
    {
      title: '补足 AI 边界认知',
      why_to_do:
        '当前你容易把 AI 当成万能的答案提供者，在它并不擅长的任务上也直接采用结果。清楚它的能力边界，才能避免把判断权过早交出去。',
      how_to_do:
        '使用前先花十秒判断：这个任务更依赖事实准确，还是更依赖表达和创意？前者交给 AI 后必须自己核查，后者可以更放心地用。每周回看一两次 AI 出错的情况，积累自己的边界清单。',
    },
    {
      title: '建立结果核查习惯',
      why_to_do:
        '你目前拿到 AI 的结果后，较少回头做事实核对和逻辑复查，多数时候直接采用。一旦它在关键数据或结论上出错，错误会原样进入你的工作产出，事后往往很难发现。',
      how_to_do:
        '给 AI 结果固定加一步复核：关键数据回到原始资料对一遍，关键结论问自己一句依据是什么。涉及对外或决策的内容，坚持人工通读后再使用，把核查变成不用思考的固定动作。',
    },
    {
      title: '练习任务拆解方法',
      why_to_do:
        '把一件完整的事一次性丢给 AI，它往往只能给出笼统、不够好用的结果。先学会把任务拆成清晰的小步，才能让 AI 在每个环节都更有针对性地发挥，产出也更贴合你的需要。',
      how_to_do:
        '拿到任务先拆成几步，逐步判断哪步适合 AI、哪步自己做，再把它们接起来。比如一份报告：调研让 AI 辅助、结构自己定、初稿 AI 写、结论自己核。多练几次就能形成稳定的人机分工习惯。',
    },
  ],
};

// ============================================================================
// scoring 块（前端合成；后端落地后由 calc_overall + calc_trait_score 真值替换）
// ============================================================================
const overall = { average: 2.59, l_grade: 'L2', l_grade_label: '基础应用级', percentage: 53 };

const abilities = [
  // 顺序遵循 dimensions/defaults.py sort_order
  { code: 'cognitive_understanding',   name: '认知理解力', layer: 'cognitive_prep', layer_label: '认知准备层', layer_sort: 1, average: 2.10, l_grade: 'L2', l_grade_label: '基础应用级', percentage: 37 },
  { code: 'learning_adaptation',       name: '学习适应力', layer: 'cognitive_prep', layer_label: '认知准备层', layer_sort: 2, average: 3.05, l_grade: 'L3', l_grade_label: '协同优化级', percentage: 68 },
  { code: 'scenario_judgment',         name: '场景判断力', layer: 'application',    layer_label: '应用执行层', layer_sort: 1, average: 2.50, l_grade: 'L2', l_grade_label: '基础应用级', percentage: 50 },
  { code: 'instruction_interaction',   name: '指令交互力', layer: 'application',    layer_label: '应用执行层', layer_sort: 2, average: 2.95, l_grade: 'L3', l_grade_label: '协同优化级', percentage: 65 },
  { code: 'task_orchestration',        name: '任务编排力', layer: 'application',    layer_label: '应用执行层', layer_sort: 3, average: 2.65, l_grade: 'L3', l_grade_label: '协同优化级', percentage: 55 },
  { code: 'result_evaluation',         name: '结果评估力', layer: 'value_output',   layer_label: '价值产出层', layer_sort: 1, average: 2.05, l_grade: 'L2', l_grade_label: '基础应用级', percentage: 35 },
  { code: 'safety_responsibility',     name: '安全责任力', layer: 'value_output',   layer_label: '价值产出层', layer_sort: 2, average: 2.20, l_grade: 'L2', l_grade_label: '基础应用级', percentage: 40 },
  { code: 'innovation_transformation', name: '创新转化力', layer: 'value_output',   layer_label: '价值产出层', layer_sort: 3, average: 2.85, l_grade: 'L3', l_grade_label: '协同优化级', percentage: 62 },
];

const layers = {
  cognitive_prep: { name: '认知准备层', average: 2.58, l_grade: 'L2', percentage: 53 },
  application:    { name: '应用执行层', average: 2.70, l_grade: 'L3', percentage: 57 },
  value_output:   { name: '价值产出层', average: 2.37, l_grade: 'L2', percentage: 46 },
};

const traits = [
  {
    code: 'ai_willingness',
    name: 'AI 意愿',
    average: 3.10, normalized: 0.70, tier: '较高水平',
    narrative: '你在工作中会主动把 AI 用起来，遇到新工具也愿意先试一试，把它当成日常完成任务的帮手，而不是等别人示范后才跟进。',
  },
  {
    code: 'independent_thinking',
    name: '独立思考',
    average: 2.20, normalized: 0.40, tier: '中等水平',
    narrative: '你大多数时候会接受 AI 给出的第一版结果，较少回头追问它的依据；在结论关系不大的小事上这样高效，但重要判断上还容易被它带着走。',
  },
  {
    code: 'proactive_learning',
    name: '主动学习',
    average: 2.80, normalized: 0.60, tier: '较高水平',
    narrative: '你会在用得上的时候去了解新的 AI 用法，遇到问题也会查一查，学习偏向被需求驱动，还没有形成定期主动更新的习惯。',
  },
];

// ============================================================================
// user_report_v5 内部对象（contract 文档 §user_report_v5 payload schema）
// ============================================================================
function makeUserReportV5({ isFallback = false, validatorFailures = [] } = {}) {
  return {
    meta: {
      report_version: 5,
      assessment_id: 0,
      depth: 'quick',
      depth_label: '基础版',
      answered_count: 30,
      total_questions: 30,
      generated_at: '2026-05-14T10:30:00+08:00',
      model: isFallback ? 'fallback' : 'glm-4-flash',
      is_fallback: isFallback,
    },
    user: {
      nickname: '示例用户',
      position: '产品主管',
      industry: '互联网',
      ai_usage_purpose: '帮助梳理需求、撰写文档初稿、生成调研要点',
    },
    overall,
    abilities,
    layers,
    traits,
    ...llmNarrative,
    quality: {
      is_fallback: isFallback,
      validator_failures: validatorFailures,
    },
  };
}

// ============================================================================
// 顶层 HTTP 响应包装（done / fallback / generating）
// ============================================================================
export const mockReportRawPayload = {
  status: 'done',
  report_version: 5,
  is_fallback: false,
  is_detailed_unlocked: false,   // 顶层解锁旗标（粗略报告默认 locked，UI 显示"解锁详细报告" CTA）
  user_report_v5: makeUserReportV5({ isFallback: false }),
};

export const mockReportFallbackPayload = {
  status: 'done',
  report_version: 5,
  is_fallback: true,
  is_detailed_unlocked: false,
  user_report_v5: makeUserReportV5({
    isFallback: true,
    validatorFailures: ['llm_call_failed_or_empty'],
  }),
};

export const mockReportGeneratingPayload = {
  status: 'generating',
  report_version: 5,
  phase: 'rough_pending',   // 粗略报告生成中
  is_detailed_unlocked: false,
  user_report_v5: null,
};

// 子块导出，便于单测 / Storybook
export const mockLLMNarrativeBlock = llmNarrative;
export const mockScoringBlock = { overall, abilities, layers, traits };
